import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { InputIcon, Search, SeriesIcon } from 'basic-components';
import { CommonService } from 'dashboard-services';
import { globalActions } from 'primary-components';

import { creatorSeriesActions, settingsSeriesActions } from 'actions/series';
import properties from 'resources/constants/properties.json';
import { routing } from 'routing';
import { AuthUtils, ExcelUtils, GAUtils } from 'utils';

import EditHeader from 'primary/EditHeader';
import HeaderNoSearch from 'primary/HeaderNoSearch';
import ProviderSelector from 'primary/ProviderSelector';
import Basket from './Basket';
import FiltersSetUp from './FiltersSetUp';
import Results from './Results';
import StartingResults from './StartingResults';
import { UserDefinedGroupFilter, UserDefinedStandardFilter } from './UserDefinedFilter';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import './Series.scss';

const emptyArray = []
const Series = () => {
  const dispatch = useDispatch(),
        { query, filters, userDefinedFilters } = useSelector(state => state.seriesState.creatorState),
        [isFiltersSetUpOpen, setIsFiltersSetUpOpen] = useState(false),
        [areFiltersChanged, setAreFiltersChanged] = useState(),
        [filtersHeight, setFiltersHeight] = useState(),
        [totalSize, setTotalSize] = useState(),
        [symbols, setSymbols] = useState(emptyArray),
        address = useSelector(state => state.seriesState.creatorState.address),
        setQuery = useCallback(query => dispatch(creatorSeriesActions.onChange(query, "query")), [dispatch]),
        setFilterValue = useCallback(({ value, name }) => dispatch(creatorSeriesActions.onChangeObject(value, name, "filters")), [dispatch]),
        symbolsFilter = useMemo(() => creatorSeriesActions.getSymbolsFilter({ filters, userDefinedFilters }), [filters, userDefinedFilters]),
        metadatasFilter = useMemo(() => creatorSeriesActions.getMetadatasFilter({ filters, userDefinedFilters }), [filters, userDefinedFilters]),
        groupFilter = useMemo(() => creatorSeriesActions.getGroupFilter({ filters, userDefinedFilters }), [filters, userDefinedFilters]),
        openFiltersSetUp = useCallback(() => setIsFiltersSetUpOpen(true), []),
        closeFiltersSetUp = useCallback(() => setIsFiltersSetUpOpen(false), []),
        getResultsStyle = useCallback((additionalHeight = 0) => {
          let defaultHeight = `calc(100% - 32px - 12px - 8px - 40px - 8px - 1px - 44px - ${additionalHeight}px`
          defaultHeight += ` - ${filtersHeight || (32 + 40 * (Number(CommonService.fixedNumber(userDefinedFilters.length % 3, 0))))}px`
          if(address) {
            defaultHeight += ` - 44px`
          }
          defaultHeight += ")"
          return {
            height: defaultHeight 
          }
        }, [address, filtersHeight, userDefinedFilters.length]),
        observer = useRef(),
        timeoutRef = useRef(),
        filtersRef = useRef(),
        isStartingResults = useMemo(() => !(query || Object.keys(metadatasFilter || {}).length > 0 || Object.keys(symbolsFilter || {}).length > 0 || groupFilter), [groupFilter, metadatasFilter, query, symbolsFilter]),
        onClose = useCallback(() => {
          dispatch(creatorSeriesActions.clear())
          dispatch(settingsSeriesActions.clear())
        }, [dispatch]),
        clearTable = useCallback(async () => {
          await ExcelUtils.clearTableWithFormulaAddress(address)
          onClose()
        }, [address, onClose]),
        refreshCall = useCallback(() => {
          GAUtils.sendEvent({
            category: "action",
            action: "refresh_call",
            label: "refresh"
          })
          return Excel.run(context =>
            ExcelUtils
              .refreshFormulaWithAdress({ address, context })
              .catch(err => {
                dispatch(AuthUtils.sendError({ error: "(RefreshCall)" + err.stack }))
                dispatch(globalActions.getMessageStore()).error(String(err))
                console.error(String(err))
                return Promise.resolve()
              })
          )
        }, [address, dispatch]),
        isSharingEnabled = useMemo(() => properties?.["sharing_enabled"] === "true", []),
        onKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            openFiltersSetUp()
          }
        }, [openFiltersSetUp])

  useEffect(() => {
    if(query) {
      GAUtils.sendEvent({
        category: "search",
        action: "search_ts",
        label: "search"
      })
    }
  }, [query])

  useEffect(() => {
    GAUtils.sendEvent({
      category: "open",
      action: "open_ts",
      label: "open"
    })
    GAUtils.sendPageView(routing.TS.name)
  }, [])

  useEffect(() => {
    setAreFiltersChanged(creatorSeriesActions.areFiltersChanged())
  }, [userDefinedFilters])

  useEffect(() => {
    const initObserver = () => {
      setFiltersHeight(filtersRef.current?.clientHeight)
      observer.current = new ResizeObserver(entries => {
        entries.forEach(() => {
          setFiltersHeight(filtersRef.current?.clientHeight)
        });
      });
      observer.current.observe(filtersRef.current, { attributes: true })
    }
    if(filtersRef.current) {
      initObserver()
    } else {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => initObserver(), 5)
    }
    return () => {
      window.clearTimeout(timeoutRef.current)
      observer.current.disconnect()
    }
  }, [])

  useEffect(() => () => {
    dispatch(creatorSeriesActions.clear())
    dispatch(settingsSeriesActions.clear())
  }, [dispatch])

  return (
    <div className={classNames(
      "ng-office-app__authed__content ng-office-app__authed__content--series",
      { "ng-office-app__authed__content ng-office-app__authed__content--edit": !!address }
    )}>
      <HeaderNoSearch
          icon={(
            <SeriesIcon
                color="grey-dark"
                height={12}
                width={12}
            />
          )}
          title="SERIES CREATOR"
      />
      <When condition={!!address}>
        {() => (
          <EditHeader 
              clearTable={clearTable}
              onClose={onClose}
              refreshCall={refreshCall}
          />
        )}
      </When>
      <div className="ng-office-app__authed__content__filters">
        <div className={classNames(
          "ng-office-app__authed__content__filters__search",
          { "ng-office-app__authed__content__filters__search--with-sharing": isSharingEnabled }
        )}>
          <When condition={isSharingEnabled}>
            {() => (
              <ProviderSelector
                  menuOffsetY={4}
                  withText={false}
              />
            )}
          </When>
          <Search
              autoFocus={false}
              defaultValue={query}
              onSearch={setQuery}
              placeholder="Search..."
              size="large"
              variant="outline"
          />
          <div 
              className={classNames(
                "ng-office-app__authed__content__filters__search__more",
                { "ng-office-app__authed__content__filters__search__more--is-changed": areFiltersChanged }
              )}
              onClick={openFiltersSetUp}
              onKeyDown={onKeyDown}
              tabIndex={0}
          >
            <InputIcon
                color="blue-bright"
                height={16}
                width={16}
            />
          </div>
        </div>
        <div 
            className="ng-office-app__authed__content__filters__filters"
            ref={filtersRef}
        >
          {userDefinedFilters.map((filter, filterIndex) => (
            <If 
                condition={filter.type === creatorSeriesActions.FILTER_TYPE.GROUP_NAME}
                key={filterIndex + filter.filterKey + userDefinedFilters?.length}
            >
              <Then>
                {() => (
                  <UserDefinedGroupFilter
                      {...filter}
                      filterKey={filter.filterKey}
                      groupFilter={groupFilter}
                      index={filterIndex}
                      metadatas={metadatasFilter}
                      // query={query}
                      setValue={setFilterValue}
                      symbols={symbolsFilter}
                      value={filters[filter.filterKey]}
                  />
                )}
              </Then>
              <Else>
                {() => (
                  <UserDefinedStandardFilter
                      {...filter}
                      filterKey={filter.filterKey}
                      groupFilter={groupFilter}
                      index={filterIndex}
                      metadatas={metadatasFilter}
                      setValue={setFilterValue}
                      symbols={symbolsFilter}
                      value={filters[filter.filterKey]}
                  />
                )}
              </Else>
            </If>
          ))}
        </div>
      </div>
      <Basket
          isStartingResults={isStartingResults}
          symbols={symbols}
          totalSize={totalSize}
      />
      <If condition={isStartingResults}>
        <Then>
          {() => (
            <StartingResults getResultsStyle={getResultsStyle}/>
          )}
        </Then>
        <Else>
          {() => (
            <Results
                getResultsStyle={getResultsStyle}
                groupFilter={groupFilter}
                metadatasFilter={metadatasFilter}
                setSymbols={setSymbols}
                setTotalSize={setTotalSize}
                symbolsFilter={symbolsFilter}
            />
          )}
        </Else>
      </If>
      <When condition={isFiltersSetUpOpen}>
        {() => (
          <FiltersSetUp onClose={closeFiltersSetUp}/>
        )}
      </When>
    </div>
  )
}

export default Series;